import type { MessageContent } from "@redotech/redo-agents-common/llm-messages";
import * as z from "zod";

/**
 * Tool name a supervisor review is injected into the reviewed thread under, so
 * message processors can recognize one without depending on the supervisor.
 */
export const SHOPPER_AGENT_SUPERVISOR_REVIEW_TOOL_NAME =
  "shopper_agent_supervisor_review";

/** Agent name on every supervisor review log, so readers can pick them out. */
export const SHOPPER_AGENT_SUPERVISOR_AGENT_NAME =
  "shopper-agent-supervisor-v1";

/** Tag the supervisor's prompt wraps around the exact draft under review. */
export const SUPERVISOR_MESSAGE_TO_SEND_TAG = "message-to-send-to-customer";

export const SupervisorDecisionSchema = z.enum(["approve", "revise", "block"]);
export type SupervisorDecision = z.infer<typeof SupervisorDecisionSchema>;

export const ShopperAgentSupervisorReviewPayloadSchema = z.object({
  decision: SupervisorDecisionSchema.describe(
    "approve when the output is safe to send, or revise when the reviewed agent should run again with feedback. block is kept only so older reviews still parse.",
  ),
  feedback: z
    .string()
    .describe(
      "Concrete feedback for the reviewed agent. For approve, briefly explain why it is grounded. For revise, explain exactly what must change or be verified.",
    ),
});
export type ShopperAgentSupervisorReviewPayload = z.infer<
  typeof ShopperAgentSupervisorReviewPayloadSchema
>;

const SupervisorReviewToolResultSchema = z.object({
  type: z.literal("tool-result"),
  toolName: z.literal(SHOPPER_AGENT_SUPERVISOR_REVIEW_TOOL_NAME),
  output: z.object({ value: ShopperAgentSupervisorReviewPayloadSchema }),
});

/** The review a message content part carries, or undefined if it isn't one. */
export function parseSupervisorReviewToolResult(
  content: MessageContent,
): ShopperAgentSupervisorReviewPayload | undefined {
  if (
    content.type !== "tool-result" ||
    content.toolName !== SHOPPER_AGENT_SUPERVISOR_REVIEW_TOOL_NAME
  ) {
    return undefined;
  }

  const parsed = SupervisorReviewToolResultSchema.safeParse(content);
  return parsed.success ? parsed.data.output.value : undefined;
}
